// Helper utilities for SettingsModal logic (language parsing/building)
import { ConfigurationAPI } from '../apis/ConfigurationAPI';
import langs from 'langs';
// Preferred name formatting utilities
function capitalizePeriodSeparated(segment) {
    const parts = segment.split('.');
    for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        if (!p)
            continue;
        parts[i] = p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
    }
    return parts.join('.');
}
function capitalizeHyphenated(word) {
    const segments = word.split('-');
    for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        if (!seg)
            continue;
        segments[i] = capitalizePeriodSeparated(seg);
    }
    return segments.join('-');
}
export function formatPreferredName(raw) {
    let cleaned = raw.replaceAll(/\s+/g, ' ').trim();
    if (!cleaned)
        return '';
    const words = cleaned.split(' ');
    for (let i = 0; i < words.length; i++) {
        words[i] = capitalizeHyphenated(words[i]);
    }
    return words.join(' ');
}
// Helper: Resolve language code via legacy fallback with uniqueness check.
function deriveLegacyLanguageCode(name, used) {
    const cleaned = name.replaceAll(/[^A-Za-z]/g, '').toLowerCase() || 'lg';
    let base = cleaned.slice(0, 2);
    let code = base;
    let attempt = 2;
    while (used.has(code)) {
        code = base + attempt;
        attempt++;
    }
    used.add(code);
    return code;
}
// Resolve a stable ISO 639-1 code from a language label using the langs library.
// Falls back to legacy derivation for unknown labels.
export function resolveLanguageCode(name, used = new Set()) {
    const key = name.trim();
    // Try to find language by name using langs library
    const lang = langs.where('name', key);
    const iso = lang?.['1']?.toLowerCase();
    if (iso) {
        used.add(iso);
        return iso;
    }
    // fallback to previous derive behavior (kept minimal) ensuring uniqueness
    return deriveLegacyLanguageCode(name, used);
}
// Safely parse a raw supported_language value into an array of strings
export function parseSupportedLanguageValue(raw) {
    let parsed = null;
    // Match strings starting with '[' or '{' to attempt JSON parse; '[' does not need escaping inside the class.
    if (/^[[{]/.test(raw)) {
        try {
            parsed = JSON.parse(raw);
        }
        catch {
            parsed = null;
        }
    }
    if (Array.isArray(parsed)) {
        return parsed
            .filter((x) => typeof x === 'string')
            .map((x) => x.trim());
    }
    return raw
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
}
// Build option list from names & ensure current selection present
export function buildLanguageOptions(names, currentSelected, existingSelected) {
    const unique = Array.from(new Set(names));
    const used = new Set();
    const opts = unique.map((n) => ({
        code: resolveLanguageCode(n, used),
        label: n
    }));
    // Normalize selected to lowercase ISO if present
    let selRaw = (currentSelected ?? existingSelected ?? 'en').trim();
    const selNorm = selRaw.toLowerCase();
    if (!opts.some((o) => o.code === selNorm)) {
        opts.push({ code: selNorm, label: selRaw });
    }
    opts.sort((a, b) => a.label.localeCompare(b.label));
    console.log('Built language options:', opts, 'Selected:', selNorm);
    return { options: opts, selected: selNorm };
}
// Extract language names from a configuration item
function extractLanguageNames(item) {
    if (item.value == null)
        return [];
    const raw = (typeof item.value === 'string' ? item.value : JSON.stringify(item.value)).trim();
    if (!raw)
        return [];
    return parseSupportedLanguageValue(raw);
}
// Extract all supported language names from configuration resources
function getAllLanguageNames(resources) {
    return resources.flatMap((resource) => resource.items
        .filter((item) => item.key === 'supported_language')
        .flatMap(extractLanguageNames));
}
// Fetch remote configurations and return language options. Throws on failure.
export async function fetchSupportedLanguagesData(currentSelected, existingSelected) {
    // Prefer filtered request; fallback to full if missing type
    let bundle = await ConfigurationAPI.getConfigurations('AppConfiguration');
    if (!bundle.resource?.some((r) => r.type === 'AppConfiguration')) {
        bundle = await ConfigurationAPI.getConfigurations();
    }
    const names = getAllLanguageNames(bundle.resource);
    console.log('Fetched supported languages:', names);
    return buildLanguageOptions(names, currentSelected, existingSelected);
}
// Check if currentLang is a valid language label (name)
function validateLanguageByName(currentLang) {
    const langByName = langs.where('name', currentLang);
    if (langByName) {
        return {
            options: [
                {
                    code: langByName['1'] || currentLang.toLowerCase(),
                    label: currentLang
                }
            ],
            selected: langByName['1'] || currentLang.toLowerCase()
        };
    }
    return null;
}
// Check if currentLang is a valid language code
function validateLanguageByCode(currentLang) {
    const langByCode = langs.where('1', currentLang.toLowerCase());
    if (langByCode) {
        return {
            options: [
                { code: currentLang.toLowerCase(), label: langByCode.name }
            ],
            selected: currentLang.toLowerCase()
        };
    }
    return null;
}
// Build fallback options when remote fetch fails.
export function buildFallbackLanguageOptions(currentSelected, existingSelected) {
    const currentLang = currentSelected ?? existingSelected ?? 'en';
    const byNameResult = validateLanguageByName(currentLang);
    if (byNameResult)
        return byNameResult;
    const byCodeResult = validateLanguageByCode(currentLang);
    if (byCodeResult)
        return byCodeResult;
    // Fallback to English if neither valid label nor code
    return {
        options: [{ code: 'en', label: 'English' }],
        selected: 'en'
    };
}
/**
 * Fetch enabled message channels from AppConfiguration
 * Returns array of channel objects with key (for backend) and label (for UI)
 */
export async function fetchEnabledMessageChannels() {
    try {
        const bundle = await ConfigurationAPI.getConfigurations('AppConfiguration');
        for (const config of bundle.resource || []) {
            if (config?.type !== 'AppConfiguration')
                continue;
            // Look for "messageChannels" configuration
            const channelItem = config.items.find((item) => item?.key === 'messageChannels');
            if (channelItem?.value) {
                // Value should be an array of {key, label, enabled} objects
                if (Array.isArray(channelItem?.value)) {
                    const channels = channelItem?.value
                        .filter((ch) => ch &&
                        typeof ch === 'object' &&
                        'key' in ch &&
                        'label' in ch &&
                        'enabled' in ch &&
                        typeof ch?.key === 'string' &&
                        typeof ch?.label === 'string' &&
                        ch?.enabled === true // Only include enabled channels
                    )
                        .map((ch) => ({
                        key: ch?.key,
                        label: ch?.label,
                        enabled: ch?.enabled
                    }));
                    return channels;
                }
            }
        }
        return [];
    }
    catch (error) {
        console.error('Failed to fetch enabled message channels:', error);
        // Return empty array on error - UI will hide message preferences section
        return [];
    }
}
/**
 * Build default message preferences object based on enabled channels
 * All channels default to false
 */
export function buildDefaultMessagePreferences(enabledChannels) {
    const preferences = {};
    for (const channel of enabledChannels) {
        preferences[channel.key] = false;
    }
    return preferences;
}
